import { LinkedInWizardData } from "@/pages/LinkedInWizard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HelpCircle, Sparkles, RefreshCw, Check } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { toast } from "sonner";
const INDUSTRIES = ["Accounting", "Advertising", "Aerospace", "Agriculture", "Architecture", "Arts & Design", "Automotive", "Aviation", "Banking", "Biotechnology", "Broadcasting", "Business Services", "Chemical", "Communications", "Computer Software", "Construction", "Consulting", "Consumer Goods", "Cybersecurity", "Data Analytics", "Defense", "E-commerce", "Education", "Electronics", "Energy", "Engineering", "Entertainment", "Environmental Services", "Fashion", "Finance", "Financial Services", "Food & Beverage", "Gaming", "Government", "Healthcare", "Hospitality", "Human Resources", "Information Technology", "Insurance", "Internet", "Investment Banking", "Legal Services", "Logistics", "Manufacturing", "Marketing", "Media", "Medical Devices", "Mining", "Nonprofit", "Oil & Gas", "Pharmaceuticals", "Public Relations", "Publishing", "Real Estate", "Renewable Energy", "Research", "Retail", "Sales", "Security", "Semiconductors", "Social Media", "Software", "Sports", "Staffing & Recruiting", "Supply Chain", "Technology", "Telecommunications", "Tourism", "Transportation", "Utilities", "Venture Capital", "Wellness", "Other"];
interface LinkedInWizardStep4Props {
  data: LinkedInWizardData;
  updateData: (updates: Partial<LinkedInWizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
}
interface HeadlineOption {
  headline: string;
  length: number;
  approach: string;
  isValid: boolean;
}
interface RecommendationData {
  recommendedIndex: number;
  score: number;
  reasons: string[];
  explanation: string;
}
export function LinkedInWizardStep4({
  data,
  updateData,
  onNext
}: LinkedInWizardStep4Props) {
  // Initialize state from existing data
  const [headlineOptions, setHeadlineOptions] = useState<HeadlineOption[]>(data.generatedHeadlines || []);
  const [selectedHeadline, setSelectedHeadline] = useState<string>(data.valueProposition || "");
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(Boolean(data.generatedHeadlines?.length));
  const [recommendation, setRecommendation] = useState<RecommendationData | null>(data.headlineRecommendation || null);
  const [customIndustry, setCustomIndustry] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const canGenerateHeadlines = data.whatYouHelp && data.targetAudience && data.howYouHelp;
  const generateHeadlines = async () => {
    if (!canGenerateHeadlines) {
      toast.error("Please fill in the first 3 required fields");
      return;
    }
    setIsGenerating(true);
    try {
      const {
        data: result,
        error
      } = await supabase.functions.invoke('generate-linkedin-headline', {
        body: {
          whatYouHelp: data.whatYouHelp,
          targetAudience: data.targetAudience,
          howYouHelp: data.howYouHelp,
          industry: Array.isArray(data.industries) ? data.industries.join(", ") : data.industries
        }
      });
      if (error) throw error;
      if (result.success && result.headlines) {
        setHeadlineOptions(result.headlines);
        setHasGenerated(true);
        // Persist headlines in wizard data
        updateData({
          generatedHeadlines: result.headlines
        });
        // Get AI recommendation
        await getHeadlineRecommendation(result.headlines);
        toast.success("Generated LinkedIn headlines!");
      } else {
        throw new Error(result.error || "Failed to generate headlines");
      }
    } catch (error) {
      console.error('Error generating headlines:', error);
      toast.error("Failed to generate headlines. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };
  const getHeadlineRecommendation = async (headlines: HeadlineOption[]) => {
    try {
      const {
        data: result,
        error
      } = await supabase.functions.invoke('get-headline-recommendation', {
        body: {
          headlines
        }
      });
      if (error) throw error;
      if (result.success && result.recommendation) {
        setRecommendation(result.recommendation);
        // Persist recommendation in wizard data
        updateData({
          headlineRecommendation: result.recommendation
        });
      }
    } catch (error) {
      console.error('Error getting recommendation:', error);
      // Don't show error toast for recommendation failure, it's not critical
    }
  };
  const selectHeadline = (headline: string) => {
    setSelectedHeadline(headline);
    updateData({
      valueProposition: headline
    });
  };
  const toggleIndustry = (industry: string) => {
    const currentIndustries = data.industries || [];
    const isSelected = currentIndustries.includes(industry);
    if (isSelected) {
      updateData({
        industries: currentIndustries.filter(i => i !== industry)
      });
    } else {
      updateData({
        industries: [...currentIndustries, industry]
      });
    }

    // Show custom input when "Other" is selected
    if (industry === "Other") {
      setShowCustomInput(!isSelected);
    }
  };
  const addCustomIndustry = () => {
    if (customIndustry.trim()) {
      const currentIndustries = data.industries || [];
      const customIndustries = customIndustry.split(',').map(i => i.trim()).filter(i => i);
      const newIndustries = [...currentIndustries, ...customIndustries];
      updateData({
        industries: [...new Set(newIndustries)] // Remove duplicates
      });
      setCustomIndustry('');
    }
  };
  const clearAllIndustries = () => {
    updateData({
      industries: []
    });
    setShowCustomInput(false);
  };
  const selectAllIndustries = () => {
    updateData({
      industries: [...INDUSTRIES]
    });
  };
  const approveAndNext = () => {
    if (selectedHeadline) {
      toast.success("Value proposition approved!");
      onNext();
    } else {
      toast.error("Please select a headline before proceeding");
    }
  };
  return <TooltipProvider>
      <div className="space-y-8">
        {/* Step Number and Header */}
        <div className="relative">
          <div className="absolute -top-2 -left-2">
            <div className="w-12 h-12 bg-linkedin rounded-full flex items-center justify-center shadow-linkedin">
              <span className="text-2xl font-bold text-white">4</span>
            </div>
          </div>
          <div className="ml-12 text-center">
            <h2 className="text-2xl font-bold mb-2 text-linkedin">Define Your Value Proposition</h2>
            <p className="mb-4 text-zinc-700">
              Answer these questions to create your powerful value statement:
            </p>
            <div className="flex justify-center items-center gap-1 text-sm text-linkedin font-medium rounded">
              <span className="bg-linkedin text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">1</span>
              <span>Who</span>
              <span className="text-muted-foreground text-xs">→</span>
              <span className="bg-linkedin text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">2</span>
              <span>What</span>
              <span className="text-muted-foreground text-xs">→</span>
              <span className="bg-linkedin text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">3</span>
              <span>How</span>
              <span className="text-muted-foreground text-xs">→</span>
              <span className="bg-linkedin text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">4</span>
              <span>Industry</span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Question 1 */}
          <Card className="p-6 bg-white/95 border-linkedin/20 relative backdrop-blur-sm">
            {/* Who badge positioned in top left corner */}
            <span className="absolute -top-2 -left-2 bg-green-600 text-white px-3 py-1 text-sm font-bold rounded-full shadow-lg z-10 border-2 border-white">
              Who
            </span>
            
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-linkedin text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">1</div>
                <div>
                  <Label htmlFor="what-you-help" className="text-lg font-semibold text-linkedin">
                    Who do you help?
                  </Label>
                  <p className="text-sm mt-1 text-zinc-700">
                    Define your target audience or client base
                  </p>
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-linkedin hover:bg-linkedin-light">
                    <HelpCircle className="w-4 h-4 mr-1" />
                    Need help?
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p className="text-sm">
                    <strong>Examples:</strong><br />
                    • Startup founders<br />
                    • Working professionals returning to workforce<br />
                    • Sales teams in various industries<br />
                    • Small business organizations<br />
                    • First-time investors
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Input id="what-you-help" placeholder="e.g., Small business owners, Tech startups, Career changers..." value={data.whatYouHelp || ""} onChange={e => updateData({
            whatYouHelp: e.target.value
          })} className="text-base border-2 border-linkedin/30 focus:border-linkedin focus:ring-2 focus:ring-linkedin/20" />
          </Card>

          {/* Question 2 */}
          <Card className="p-6 bg-white/95 border-linkedin/20 relative backdrop-blur-sm">
            {/* What badge positioned in top left corner */}
            <span className="absolute -top-2 -left-2 bg-green-600 text-white px-3 py-1 text-sm font-bold rounded-full shadow-lg z-10 border-2 border-white">
              What
            </span>
            
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-linkedin text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">2</div>
                <div>
                  <Label htmlFor="target-audience" className="text-lg font-semibold text-linkedin">
                    What do you help them achieve?
                  </Label>
                  <p className="text-sm mt-1 text-zinc-700">
                    Focus on specific outcomes and results
                  </p>
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-linkedin hover:bg-linkedin-light">
                    <HelpCircle className="w-4 h-4 mr-1" />
                    Need help?
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p className="text-sm">
                    <strong>Examples:</strong><br />
                    • Increase revenue by 50% in 6 months<br />
                    • Land their dream job in 90 days<br />
                    • Scale their operations efficiently<br />
                    • Build sustainable donor relationships<br />
                    • Generate passive income streams
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Input id="target-audience" placeholder="e.g., Double their revenue, Land their dream job, Scale efficiently..." value={data.targetAudience || ""} onChange={e => updateData({
            targetAudience: e.target.value
          })} className="text-base border-2 border-linkedin/30 focus:border-linkedin focus:ring-2 focus:ring-linkedin/20" />
          </Card>

          {/* Question 3 */}
          <Card className="p-6 bg-white/95 border-linkedin/20 relative backdrop-blur-sm">
            {/* How badge positioned in top left corner */}
            <span className="absolute -top-2 -left-2 bg-green-600 text-white px-3 py-1 text-sm font-bold rounded-full shadow-lg z-10 border-2 border-white">
              How
            </span>
            
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-linkedin text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">3</div>
                <div>
                  <Label htmlFor="how-you-help" className="text-lg font-semibold text-linkedin">
                    How do you do it?
                  </Label>
                  <p className="text-sm mt-1 text-zinc-700">
                    Your unique approach, method, or expertise
                  </p>
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-linkedin hover:bg-linkedin-light">
                    <HelpCircle className="w-4 h-4 mr-1" />
                    Need help?
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p className="text-sm">
                    <strong>Examples:</strong><br />
                    • Strategic consulting and data-driven insights<br />
                    • Personalized coaching and proven frameworks<br />
                    • Cutting-edge technology solutions<br />
                    • Comprehensive training programs<br />
                    • Expert guidance and industry connections
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Input id="how-you-help" placeholder="e.g., Strategic consulting, Proven frameworks, Expert coaching..." value={data.howYouHelp || ""} onChange={e => updateData({
            howYouHelp: e.target.value
          })} className="text-base border-2 border-linkedin/30 focus:border-linkedin focus:ring-2 focus:ring-linkedin/20" />
          </Card>

          {/* Question 4 - Industry */}
          <Card className="p-6 bg-white/95 border-linkedin/20 relative backdrop-blur-sm">
            {/* Optional badge positioned in top left corner */}
            <span className="absolute -top-2 -left-2 bg-linkedin text-white px-3 py-1 text-sm font-bold rounded-full shadow-lg z-10 border-2 border-white">
              Optional
            </span>
            
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-linkedin text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">4</div>
                <div>
                  <Label className="text-lg font-semibold text-linkedin">
                    What industry do you work in?
                  </Label>
                  <p className="text-sm text-zinc-700">
                    This helps contextualize your expertise (select all that apply)
                  </p>
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-linkedin hover:bg-linkedin-light">
                    <HelpCircle className="w-4 h-4 mr-1" />
                    Need help?
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p className="text-sm">
                    <strong>Select relevant industries:</strong><br />
                    • Technology & Software<br />
                    • Healthcare & Medical<br />
                    • Finance & Banking<br />
                    • Education & Training<br />
                    • Marketing & Advertising<br />
                    • Real Estate<br />
                    • Consulting
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={selectAllIndustries} className="text-xs border-linkedin/30 text-linkedin hover:bg-linkedin-light">
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={clearAllIndustries} className="text-xs border-linkedin/30 text-linkedin hover:bg-linkedin-light">
                Clear All
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 max-h-60 overflow-y-auto border rounded-lg p-3 bg-linkedin-light/30 border-linkedin/20">
              {INDUSTRIES.map(industry => {
              const isSelected = data.industries?.includes(industry);
              return <Card key={industry} className={`p-2 cursor-pointer transition-all hover:shadow-sm border-linkedin/20 ${isSelected ? 'border-green-500 bg-green-50 dark:bg-green-950 ring-2 ring-green-200 shadow-sm' : 'border-linkedin/30 bg-linkedin-light/10 hover:border-linkedin/50 hover:bg-linkedin-light/20'}`} onClick={() => toggleIndustry(industry)}>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium ${isSelected ? 'text-green-800 dark:text-green-200' : 'text-linkedin dark:text-linkedin'}`}>
                        {industry}
                      </span>
                      {isSelected && <Badge variant="default" className="bg-green-600 text-white text-xs px-2 py-0">
                          ✓
                        </Badge>}
                    </div>
                  </Card>;
            })}
            </div>
            
            {/* Custom Industry Input */}
            {(showCustomInput || data.industries?.includes("Other")) && <div className="mt-4 p-4 bg-linkedin-light/50 rounded-lg border border-linkedin/20">
                <Label className="text-sm font-medium mb-2 block text-linkedin">
                  Add Custom Industries
                </Label>
                <p className="text-xs text-linkedin/70 mb-3">
                  Enter your custom industry or multiple industries separated by commas
                </p>
                <div className="flex gap-2">
                  <Input value={customIndustry} onChange={e => setCustomIndustry(e.target.value)} placeholder="e.g., PropTech, AgriTech, or multiple industries separated by commas" className="flex-1 text-sm border-linkedin/20 focus:border-linkedin" onKeyPress={e => e.key === 'Enter' && addCustomIndustry()} />
                  <Button onClick={addCustomIndustry} disabled={!customIndustry.trim()} size="sm" className="bg-linkedin hover:bg-linkedin-hover text-white">
                    Add
                  </Button>
                </div>
              </div>}
            
            {!showCustomInput && !data.industries?.includes("Other") && <div className="mt-3">
                <Button variant="ghost" size="sm" onClick={() => setShowCustomInput(true)} className="text-linkedin hover:text-linkedin hover:bg-linkedin-light text-xs">
                  + Add custom industry
                </Button>
              </div>}

            {data.industries && data.industries.length > 0 && <div className="mt-4 p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                  Selected Industries ({data.industries.length}):
                </p>
                <div className="flex flex-wrap gap-2">
                  {data.industries.map((industry, index) => <Badge key={`${industry}-${index}`} variant="secondary" className="text-xs bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer" onClick={() => toggleIndustry(industry)}>
                      {industry} ✕
                    </Badge>)}
                </div>
                <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                  Click on any industry tag to remove it
                </p>
              </div>}
          </Card>
        </div>

        {/* Visual Separator - Clear distinction between setup and headline generation */}
        <div className="relative my-12">
          <div className="w-full border-t-2 border-dashed border-linkedin/30 mb-6"></div>
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-linkedin/10 to-linkedin/20 px-6 py-3 rounded-full border-2 border-linkedin/30">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-linkedin" />
                <span className="text-lg font-bold text-linkedin">Now Let's Generate Your LinkedIn Headlines</span>
                <Sparkles className="w-5 h-5 text-linkedin" />
              </div>
            </div>
          </div>
        </div>

        {/* Generate Headlines Section */}
        {/* Show selected headline if one exists */}
        {data.valueProposition && !hasGenerated && <Card className="p-6 bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800 mb-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Check className="w-6 h-6 text-green-600" />
                <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                  Your Current LinkedIn Headline
                </h3>
              </div>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-green-200">
                <p className="text-base font-medium text-gray-900 dark:text-gray-100 leading-relaxed">
                  {data.valueProposition}
                </p>
              </div>
              <div className="flex gap-3">
                <Button onClick={generateHeadlines} disabled={isGenerating} className="bg-linkedin hover:bg-linkedin/90 text-white">
                  {isGenerating ? <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </> : <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Generate New Options
                    </>}
                </Button>
                <Button variant="outline" onClick={() => {
              setSelectedHeadline("");
              updateData({
                valueProposition: ""
              });
            }}>
                  Clear Selection
                </Button>
              </div>
            </div>
          </Card>}

        {canGenerateHeadlines && !hasGenerated && !data.valueProposition && <Card className="p-8 bg-gradient-to-r from-linkedin/10 to-linkedin/5 border-linkedin/30">
            <div className="text-center space-y-4">
              <div>
                <h3 className="text-xl font-bold text-linkedin mb-2">Ready to Generate Your Headlines?</h3>
                <p className="text-muted-foreground">
                  Create optimized LinkedIn headlines tailored to your expertise
                </p>
              </div>
              <Button onClick={generateHeadlines} disabled={isGenerating} size="lg" className="px-8 py-3 shadow-lg bg-linkedin hover:bg-linkedin/90 text-white">
                {isGenerating ? <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Generating Headlines...
                  </> : <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate AI-Powered Headlines
                  </>}
              </Button>
            </div>
          </Card>}

        {/* Headlines Selection */}
        {hasGenerated && headlineOptions.length > 0 && <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-linkedin">
                Choose Your LinkedIn Headline
              </h3>
              <Button variant="outline" onClick={generateHeadlines} disabled={isGenerating} className="bg-linkedin text-white hover:bg-linkedin/90 border-linkedin">
                {isGenerating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Regenerate Headlines
              </Button>
            </div>
            
            <div className="space-y-6">
              {headlineOptions.map((option, index) => <div key={index} className="relative">
                  {/* Bold Badge - positioned outside the card with higher z-index */}
                  <div className="absolute -top-3 left-6 bg-linkedin text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg z-20 border-2 border-white">
                    Headline Option #{index + 1}
                  </div>
                  
                  {/* Recommended Badge */}
                  {recommendation && recommendation.recommendedIndex === index && <div className="absolute -top-3 right-6 bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg z-20 border-2 border-white">
                      ⭐ Our Pick
                    </div>}

                  <Card className={`p-6 cursor-pointer transition-all duration-200 pt-10 relative z-10 border-2 ${selectedHeadline === option.headline ? 'border-green-500 bg-green-50 ring-2 ring-green-200 shadow-lg' : 'border-linkedin/40 bg-white hover:border-linkedin hover:bg-linkedin/5 hover:shadow-md'}`} onClick={() => selectHeadline(option.headline)}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-base font-medium text-gray-900 leading-relaxed mb-3">
                          {option.headline}
                        </p>
                        <div className="flex items-center gap-4">
                          <span className={`text-sm font-medium ${option.length <= 220 ? 'text-green-600' : 'text-red-600'}`}>
                            {option.length}/220 characters
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {option.approach}
                          </span>
                        </div>
                      </div>
                      {selectedHeadline === option.headline && <div className="ml-3 text-green-600 flex-shrink-0">
                          <Check className="w-6 h-6" />
                        </div>}
                    </div>
                  </Card>
                </div>)}
            </div>

            {/* AI Recommendation Section */}
            {recommendation && <Card className="p-6 bg-gradient-to-r from-green-50 to-green-100 border-green-200 mt-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-600 text-white rounded-full p-2">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <h4 className="text-xl font-bold text-green-800">Our Pick & Recommendation</h4>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                        Option #{recommendation.recommendedIndex + 1}
                      </div>
                      <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                        Score: {recommendation.score}/100
                      </div>
                    </div>
                    <p className="text-gray-900 font-medium mb-3 italic">
                      "{headlineOptions[recommendation.recommendedIndex]?.headline}"
                    </p>
                    <div className="text-green-700 text-sm space-y-2">
                      <p className="font-semibold">Why we recommend this headline:</p>
                      <p className="leading-relaxed">{recommendation.explanation}</p>
                      <div className="mt-3">
                        <p className="font-semibold mb-1">Key strengths:</p>
                        <ul className="list-disc list-inside space-y-1">
                          {recommendation.reasons.map((reason, idx) => <li key={idx}>{reason}</li>)}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>}

            {/* Action Buttons - Always show after headlines are generated */}
            <div className="flex justify-center gap-4 mt-8">
              <Button onClick={generateHeadlines} disabled={isGenerating} size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3">
                {isGenerating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Regenerate Headlines
              </Button>
              
              <Button onClick={approveAndNext} disabled={!selectedHeadline} size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed">
                <Check className="w-5 h-5 mr-2" />
                Approve & Continue
              </Button>
            </div>
          </div>}
      </div>
    </TooltipProvider>;
}